import React from 'react';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Card, Spin } from 'antd';
import { KpiTile, KpiTileData } from '~analytics-components/kpi-tile';
import style from './style.module.scss';

export interface KpiCardProps {
    title: string;
    isLoading?: boolean;
    kpis: KpiTileData[];
    layout?: 'auto' | 'stacked';
    infoLines?: string[];
}

export const KpiCard: React.FC<KpiCardProps> = ({
    title,
    isLoading = false,
    kpis,
    layout = 'auto',
    infoLines = [],
}) => {
    const renderContent = () => {
        if (isLoading) {
            return (
                <div className={style.kpi_card_spinner}>
                    <Spin />
                </div>
            );
        }

        const [firstKpi, secondKpi, ...remainingKpis] = kpis;

        // Not even one KPI, nothing to render.
        if (!firstKpi) return null;

        // Only one KPI, render it full-width.
        if (!secondKpi) {
            return (
                <div className={style.kpi_card_content}>
                    <KpiTile kpi={firstKpi} />
                </div>
            );
        }

        // Stacked layout: first full-width, rest full-width below
        if (layout === 'stacked') {
            return (
                <div
                    className={`${style.kpi_layout_3} ${style.kpi_card_content}`}
                >
                    <KpiTile kpi={firstKpi} />
                    <KpiTile kpi={secondKpi} />
                    {remainingKpis.map((kpi, index) => (
                        <KpiTile key={index + 2} kpi={kpi} />
                    ))}
                </div>
            );
        }

        // We have at least 2 KPIs. If no remaining KPIs (only 2), render 2 in a row.
        if (remainingKpis.length === 0) {
            return (
                <div className={`${style.kpi_row_2} ${style.kpi_card_content}`}>
                    <KpiTile kpi={firstKpi} />
                    <KpiTile kpi={secondKpi} />
                </div>
            );
        }

        // We have 3+ KPIs: first full-width, rest in row
        return (
            <div className={`${style.kpi_layout_3} ${style.kpi_card_content}`}>
                <KpiTile kpi={firstKpi} />
                <div className={style.kpi_row_2}>
                    <KpiTile kpi={secondKpi} />
                    {remainingKpis.map((kpi, index) => (
                        <KpiTile key={index + 2} kpi={kpi} />
                    ))}
                </div>
            </div>
        );
    };

    return (
        <Card className={style.kpi_card}>
            <span className={`${style.kpi_card_title} body_bold`}>{title}</span>
            {renderContent()}
            {!isLoading && infoLines.length > 0 && (
                <div className={style.kpi_card_info}>
                    {infoLines.map((line) => (
                        <div key={line} className={style.kpi_card_info_line}>
                            <ExclamationCircleOutlined
                                className={style.kpi_card_info_icon}
                            />
                            <span className={style.kpi_card_info_text}>
                                {line}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </Card>
    );
};
